import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for handling async operations with loading, error, and data states
 *
 * @param {Function} asyncFunction - The async function to execute
 * @param {boolean} immediate - Whether to execute immediately on mount
 * @returns {Object} Async state and execute function
 *
 * @example
 * const { loading, error, data, execute } = useAsync(fetchUsers);
 *
 * // Execute manually
 * useEffect(() => {
 *   execute();
 * }, []);
 *
 * // Or with immediate execution
 * const { loading, data } = useAsync(fetchUsers, true);
 */
export const useAsync = (asyncFunction, immediate = false) => {
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  /**
   * Execute the async function
   * @param {...any} params - Parameters to pass to the async function
   * @returns {Promise} The result or throws error
   */
  const execute = useCallback(
    async (...params) => {
      try {
        setLoading(true);
        setError(null);
        const result = await asyncFunction(...params);
        setData(result);
        return result;
      } catch (err) {
        setError(err);
        console.error('Async error:', err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [asyncFunction]
  );

  /**
   * Reset the state to initial values
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setData(null);
  }, []);

  return {
    loading,
    error,
    data,
    execute,
    reset
  };
};

/**
 * Custom hook for fetching data with automatic execution on mount
 *
 * @param {Function} fetchFunction - The async function to fetch data
 * @param {Array} dependencies - Dependencies that trigger refetch
 * @returns {Object} Fetch state
 *
 * @example
 * const { loading, error, data, refetch } = useFetch(
 *   () => api.get('/users'),
 *   [] // dependencies
 * );
 */
export const useFetch = (fetchFunction, dependencies = []) => {
  const { loading, error, data, execute } = useAsync(fetchFunction);

  // Execute on mount and when dependencies change
  useEffect(() => {
    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    loading,
    error,
    data,
    refetch: execute
  };
};

export default useAsync;
